"use client";

import { useState, useTransition } from "react";
import { useTranslations, useLocale } from "next-intl";
import { createBooking } from "./actions";

type PackageOption = { id: string; name: string; priceNok: number };
type DepartureOption = {
  id: string;
  label: string;
  seatsLeft: number;
  blockedPackageIds: string[];
};

export function BookingForm({
  tourSlug,
  packages,
  departures,
}: {
  tourSlug: string;
  packages: PackageOption[];
  departures: DepartureOption[];
}) {
  const t = useTranslations("Booking");
  const locale = useLocale();
  const [departureId, setDepartureId] = useState(departures[0]?.id ?? "");
  const [packageId, setPackageId] = useState(packages[0]?.id ?? "");
  const [customerName, setCustomerName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [seats, setSeats] = useState(1);
  const [allergies, setAllergies] = useState("");
  const [isPending, startTransition] = useTransition();
  const [result, setResult] = useState<null | {
    success: boolean;
    error?: string;
  }>(null);

  const selectedDeparture = departures.find((d) => d.id === departureId);
  const notEnoughSeats = selectedDeparture
    ? seats > selectedDeparture.seatsLeft
    : false;
  const availablePackages = packages.filter(
    (p) => !selectedDeparture?.blockedPackageIds.includes(p.id)
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setResult(null);
    startTransition(async () => {
      const res = await createBooking({
        tourSlug,
        locale,
        departureId,
        packageId,
        customerName,
        email,
        phone: phone || undefined,
        seats,
        allergies: allergies || undefined,
      });
      setResult(res);
      if (res.success) {
        setCustomerName("");
        setEmail("");
        setPhone("");
        setSeats(1);
        setAllergies("");
      }
    });
  }

  if (departures.length === 0) {
    return null;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-6 space-y-4 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200"
    >
      <h3 className="text-lg font-semibold text-slate-800">{t("title")}</h3>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          {t("departureLabel")}
        </label>
        <select
          value={departureId}
          onChange={(e) => setDepartureId(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-slate-800"
        >
          {departures.map((d) => (
            <option key={d.id} value={d.id} disabled={d.seatsLeft <= 0}>
              {d.label} —{" "}
              {d.seatsLeft > 0
                ? `${d.seatsLeft} ${t("seatsLeftShort")}`
                : t("full")}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          {t("packageLabel")}
        </label>
        <select
          value={packageId}
          onChange={(e) => setPackageId(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-slate-800"
        >
          {availablePackages.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.priceNok} kr
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          {t("nameLabel")}
        </label>
        <input
          required
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-slate-800"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          {t("emailLabel")}
        </label>
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-slate-800"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          {t("phoneLabel")}
        </label>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-slate-800"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          {t("seatsLabel")}
        </label>
        <input
          required
          type="number"
          min={1}
          value={seats}
          onChange={(e) => setSeats(Number(e.target.value))}
          className="mt-1 w-24 rounded-lg border border-slate-300 p-2 text-slate-800"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          {t("allergiesLabel")}
        </label>
        <textarea
          value={allergies}
          onChange={(e) => setAllergies(e.target.value)}
          rows={2}
          className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-slate-800"
        />
      </div>

      {notEnoughSeats && (
        <p className="text-sm text-red-600">{t("notEnoughSeats")}</p>
      )}

      {result && !result.success && (
        <p className="text-sm text-red-600">
          {result.error === "not_enough_seats"
            ? t("notEnoughSeats")
            : t("genericError")}
        </p>
      )}

      {result?.success && (
        <p className="text-sm font-medium text-emerald-700">{t("success")}</p>
      )}

      <button
        type="submit"
        disabled={isPending || notEnoughSeats || !departureId}
        className="w-full rounded-lg bg-slate-800 py-2.5 font-medium text-white transition hover:bg-slate-700 disabled:opacity-50"
      >
        {isPending ? t("submitting") : t("submit")}
      </button>
    </form>
  );
}
